import React from 'react';
import { useAuction } from '../hooks/useAuction';
import type { AuctionStatus, Player, Team } from '../types';
import { formatCurrency } from '../utils/helpers';
import { Users, Gavel } from 'lucide-react';

interface PublicDisplayProps {
  players: Player[];
  teams: Team[];
}

const PublicDisplay: React.FC<PublicDisplayProps> = ({ players, teams }) => {
  const { auction } = useAuction({ players, teams, currentUser: null });
  const activeDisplayStatuses: Array<AuctionStatus> = ['bidding', 'paused', 'sold', 'unsold', 'going_once', 'going_twice'];
  const player = activeDisplayStatuses.includes(auction.status) ? auction.currentPlayer : null;

  if (!player) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
        <div className="text-center space-y-6">
          <div className="w-32 h-32 bg-indigo-600 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <Gavel className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-white tracking-tight">Live Auction</h1>
          <p className="text-slate-400 text-xl">
            {auction.status === 'idle' && auction.currentPlayer
              ? 'Waiting for bidding to start...'
              : 'Waiting for the next player...'}
          </p>
        </div>
      </div>
    );
  }

  const formatProperCase = (value: string) =>
    value
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  const isSold = auction.status === 'sold';
  const isUnsold = auction.status === 'unsold';
  const showCurrentBid = auction.showCurrentBid;
  const playingSide = player.details?.playing_side || '-';
  const bidHolderName = auction.currentBidder?.name || 'No bids yet';
  const playerName = formatProperCase(player.name);
  const formattedBid = formatCurrency(auction.currentBid);

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-hidden flex flex-col">
      {/* Header / Banner */}
      <div className="h-20 bg-gradient-to-r from-indigo-900 to-slate-900 border-b border-white/10 flex items-center justify-between px-8">
        <div className="flex items-center gap-3">
          <Gavel className="w-8 h-8 text-yellow-400" />
          <h1 className="text-2xl font-bold tracking-wider uppercase">Live Auction</h1>
        </div>
        {auction.status === 'bidding' && (
           <div className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-full animate-pulse border border-red-500/30">
             <span className="w-2 h-2 bg-red-500 rounded-full"></span>
             <span className="font-bold tracking-wider">LIVE BIDDING</span>
           </div>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
           <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-900 to-slate-900"></div>
        </div>

        <div className="relative w-full max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left Column: Player Image */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative group">
              <div className="absolute inset-0 bg-indigo-500 rounded-2xl rotate-3 opacity-20 group-hover:rotate-6 transition-transform duration-700"></div>
              <div className="absolute inset-0 bg-purple-500 rounded-2xl -rotate-3 opacity-20 group-hover:-rotate-6 transition-transform duration-700"></div>

              <div className="relative w-[300px] h-[300px] md:w-[450px] md:h-[450px] bg-slate-800 rounded-2xl overflow-hidden border-4 border-white/10 shadow-2xl">
                {player.details?.photo_url ? (
                  <img
                    src={player.details.photo_url}
                    alt={playerName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-slate-800">
                    <Users className="w-32 h-32 text-slate-600" />
                  </div>
                )}

                {/* Status Overlays */}
                {isSold && (
                  <div className="absolute inset-0 bg-green-900/80 backdrop-blur-sm flex flex-col items-center justify-center animate-in fade-in zoom-in duration-300">
                    <div className="border-8 border-green-400 rounded-full p-8 rotate-[-12deg]">
                      <span className="text-6xl md:text-8xl font-black text-green-400 uppercase tracking-widest">SOLD</span>
                    </div>
                  </div>
                )}
                {isUnsold && (
                  <div className="absolute inset-0 bg-red-900/80 backdrop-blur-sm flex flex-col items-center justify-center animate-in fade-in zoom-in duration-300">
                    <div className="border-8 border-red-400 rounded-full p-8 rotate-[12deg]">
                      <span className="text-6xl md:text-8xl font-black text-red-400 uppercase tracking-widest">UNSOLD</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Details */}
          <div className="lg:col-span-7 space-y-8 text-center lg:text-left">
            <div>
              <h2 className="text-5xl md:text-7xl font-black text-white mb-4 leading-tight">
                {playerName}
              </h2>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-xl text-slate-300 font-medium">
                {player.details?.player_role && (
                  <span className="px-4 py-2 bg-white/10 rounded-lg border border-white/10">
                    {player.details.player_role}
                  </span>
                )}
                {player.details?.batting_style && (
                  <span className="px-4 py-2 bg-white/10 rounded-lg border border-white/10">
                    {player.details.batting_style}
                  </span>
                )}
                {player.details?.bowling_style && (
                  <span className="px-4 py-2 bg-white/10 rounded-lg border border-white/10">
                    {player.details.bowling_style}
                  </span>
                )}
              </div>
            </div>

            {/* Auction Status & Stats */}
            <div className="grid grid-cols-1 gap-6">
              <div className="bg-slate-800/50 backdrop-blur-md border border-white/10 rounded-2xl p-8">
                {!isSold && !isUnsold ? (
                  showCurrentBid ? (
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                      <div>
                        <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Current Bid</p>
                        <p className="text-6xl font-black text-green-400 tabular-nums">
                          {formattedBid}
                        </p>
                      </div>
                      <div className="flex flex-col sm:items-end gap-3">
                        <span className="px-4 py-2 rounded-full bg-indigo-500/20 text-indigo-200 text-sm font-semibold border border-indigo-400/40">
                          {auction.status === 'bidding' ? 'Bidding Live' : 'Awaiting Next Bid'}
                        </span>
                        <div className="text-left sm:text-right">
                          <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Bid Holder</p>
                          <p className="text-2xl font-bold text-white leading-tight">{bidHolderName}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-center gap-3">
                      <span className="px-4 py-2 rounded-full bg-indigo-500/20 text-indigo-200 text-sm font-semibold border border-indigo-400/40">
                        {auction.status === 'bidding' ? 'Bidding Live' : 'Awaiting Next Bid'}
                      </span>
                    </div>
                  )
                ) : isSold ? (
                   <div className="text-center space-y-4">
                     <p className="text-slate-400 text-lg uppercase tracking-wider">Sold To</p>
                     <p className="text-5xl font-black text-white">{auction.currentBidder?.name}</p>
                    <div className="inline-block px-6 py-2 bg-green-500/20 text-green-400 rounded-full text-2xl font-bold border border-green-500/30">
                      {formattedBid} Points
                    </div>
                   </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-3xl font-bold text-red-400">Passed / Unsold</p>
                  </div>
                )}
              </div>

              <div className="bg-slate-800/50 backdrop-blur-md border border-white/10 rounded-2xl p-8 h-full flex flex-col items-center justify-center text-center">
                <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Playing Side</p>
                <p className="text-3xl font-bold capitalize">{playingSide}</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDisplay;
